import { Spacing } from '@persona-id/design-tokens';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { COLORS } from 'shared/styles/variables';
import styled, { css, ThemeContext } from 'styled-components';

import IconChevron from 'images/icons/chevron.svg';
import ImageCar from 'images/verify/id-class/car.svg';
import ImageFlag from 'images/verify/id-class/flag.svg';
import ImageGovernment from 'images/verify/id-class/government.svg';
import ImagePlane from 'images/verify/id-class/plane.svg';
import Button from 'inquiry/components/Button';
import { GovernmentIdClass } from 'lib/constants/governmentIds';
import { CountryCode } from 'lib/constants/locales';
import { tForGovernmentId } from 'lib/governmentId';
import { IconStyle, VerticalOptionsStyle } from 'lib/interfaces/ThemeSerialized';

const OptionBlock = styled('div')<{ verticalOptionsStyle: VerticalOptionsStyle }>`
  display: flex;
  justify-content: space-between;
  align-items: center;

  cursor: pointer;

  ${({ verticalOptionsStyle }) => {
    switch (verticalOptionsStyle) {
      case VerticalOptionsStyle.Default:
        return css`
          padding-top: ${Spacing[4]};
          padding-bottom: ${Spacing[4]};
          padding-left: ${Spacing[5]};
          padding-right: ${Spacing[5]};
          border-bottom: 1px solid ${COLORS.gray50};
          :first-child {
            border-top: 1px solid ${COLORS.gray50};
          }

          :hover,
          :focus {
            background-color: ${({ theme }) => theme.colorDropdownOption};
          }
        `;
      case VerticalOptionsStyle.Light:
        // This custom theme is specifically for DoorDash. That's why various variables are hardcoded.
        return css`
          padding-top: 18px;
          padding-bottom: 18px;
          padding-left: 0;
          padding-right: ${Spacing[5]};
          border-bottom: 1px solid ${({ theme }) => theme.colorDivider};
          :first-child {
            border-top: 1px solid ${({ theme }) => theme.colorDivider};
          }

          color: ${({ theme }) => theme.colorFontTitle};
          margin-left: ${Spacing[5]};
          font-weight: 500;

          /* Note no hover styles; focus-within is keyboard only for a11y */
          :focus-within {
            background-color: ${({ theme }) => theme.colorDropdownOption};
          }
        `;
    }
  }}
`;

const ThemedIcon = styled('span')`
  rect[fill]:not([fill='none']) {
    fill: ${(props) => props.theme.iconColorGovernmentIdType};
  }

  path[fill]:not([fill='none']) {
    fill: ${(props) => props.theme.colorModalBackground};
  }

  height: 32px;
  margin-right: ${Spacing[4]};
`;

const Text = styled('span')`
  flex-grow: 1;
`;

const OptionVerticalButton = styled(Button)`
  width: 100%;
  & + & {
    margin-top: ${Spacing[5]};
  }
`;

interface OptionVerticalProps {
  countryCode: CountryCode;
  handleSelectType: (type: string) => void;
  governmentIdClass: GovernmentIdClass;
}

export default function OptionVertical(props: OptionVerticalProps) {
  const { iconStyle, verticalOptionsStyle } = useContext(ThemeContext);

  const { countryCode, governmentIdClass } = props;

  const [t] = useTranslation();

  if (verticalOptionsStyle === VerticalOptionsStyle.Button) {
    return (
      <OptionVerticalButton
        id={`select__option--${governmentIdClass}`}
        data-test='option-block'
        key={governmentIdClass}
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        onClick={() => props.handleSelectType(governmentIdClass)}
      >
        {tForGovernmentId(t, ['government-id.title'], countryCode, governmentIdClass)}
      </OptionVerticalButton>
    );
  }

  let imageIcon;
  switch (governmentIdClass) {
    case 'dl':
      imageIcon = <ImageCar />;
      break;

    case 'ofw':
    case 'pp':
    case 'ppc':
    case 'visa':
      imageIcon = <ImagePlane />;
      break;

    case 'id':
      imageIcon = <ImageFlag />;
      break;

    case 'rp':
    case 'pr':
    default:
      imageIcon = <ImageGovernment />;
      break;
  }

  return (
    <OptionBlock
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      verticalOptionsStyle={verticalOptionsStyle}
      id={`select__option--${governmentIdClass}`}
      data-test='option-block'
      key={governmentIdClass}
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      onClick={() => props.handleSelectType(governmentIdClass)}
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      onKeyPress={() => props.handleSelectType(governmentIdClass)}
      tabIndex={0}
      role='button'
    >
      {iconStyle !== IconStyle.None && (
        <ThemedIcon data-test='option-vertical__pictograph' aria-hidden>
          {imageIcon}
        </ThemedIcon>
      )}
      <Text>{tForGovernmentId(t, ['government-id.title'], countryCode, governmentIdClass)}</Text>
      <IconChevron aria-hidden />
    </OptionBlock>
  );
}
